// Imports
import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import api from './api';
import routes_api from './routes_api';
import routes_web from './routes_web';
import { decodeJWT, isTokenExpired, getUserIdFromToken, isTokenValidLocally } from '../offline_items/jwtUtils';
import { wipeAllLocalData, wipeOfflineData } from '../offline_items/wipeLocalData';

// Last user that logged in on this browser (detects a user switch on login)
const LAST_USER_ID_KEY = 'last_user_id';

// ##### HELPER: Revoke the refresh token on the server (blacklist)
// Plain axios (not `api`): no Bearer header, so an expired access token can't trigger a 401/refresh loop.
// `wipe` asks the server for a Clear-Site-Data header. Never throws: logout must work offline too.
const revokeRefreshToken = async (refreshToken, { wipe = false } = {}) => {
  if (!refreshToken || !navigator.onLine) return;
  try {
    await axios.post(
      routes_api.accounts_logout,
      { refresh_token: refreshToken, wipe },
      { baseURL: api.defaults.baseURL, timeout: 5000 }
    );
  } catch (error) {
    console.warn('Failed to revoke refresh token on server:', error?.message);
  }
};

// Create context
const AuthContext = createContext();

// ##### HELPER: Get cached user synchronously (for initial state)
const getCachedUserSync = () => {
  try {
    const cached = localStorage.getItem('user_data_cache');
    if (cached) {
      const userData = JSON.parse(cached);
      // Check if cache is still valid (24 hours)
      const cacheTime = localStorage.getItem('user_data_cache_time');
      if (cacheTime && Date.now() - parseInt(cacheTime) < 24 * 60 * 60 * 1000) {
        return userData;
      }
    }
  } catch (error) {
    console.error('Failed to load cached user on mount:', error);
  }
  return null;
};

// Define provider
export const AuthProvider = ({ children }) => {
  // ##### OPTIMIZATION: Initialize user from cache synchronously (avoids spinner)
  const [user, setUser] = useState(() => getCachedUserSync());
  const [login_error, setLoginError] = useState('');
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const navigate = useNavigate();

  // ##### GET CACHED USER DATA
  const getCachedUser = useCallback(() => {
    try {
      const cached = localStorage.getItem('user_data_cache');
      if (cached) {
        const userData = JSON.parse(cached);
        // Check if cache is still valid (24 hours)
        const cacheTime = localStorage.getItem('user_data_cache_time');
        if (cacheTime && Date.now() - parseInt(cacheTime) < 24 * 60 * 60 * 1000) {
          return userData;
        }
      }
    } catch (error) {
      console.error('Failed to get cached user:', error);
    }
    return null;
  }, []);

  // ##### CACHE USER DATA
  const cacheUser = useCallback((userData) => {
    try {
      localStorage.setItem('user_data_cache', JSON.stringify(userData));
      localStorage.setItem('user_data_cache_time', Date.now().toString());
    } catch (error) {
      console.error('Failed to cache user data:', error);
    }
  }, []);

  // ##### CLEAR CACHED USER DATA
  const clearCachedUser = useCallback(() => {
    try {
      localStorage.removeItem('user_data_cache');
      localStorage.removeItem('user_data_cache_time');
    } catch (error) {
      console.error('Failed to clear cached user:', error);
    }
  }, []);

  // Logout function (keeps offline data on this device)
  const logout = useCallback((redirect = true) => {
    revokeRefreshToken(localStorage.getItem('refresh_token'));
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    clearCachedUser(); // Clear cached user data on logout
    toast('User is Logged out');
    setUser(null);
    if (redirect) navigate(routes_web.accounts_login);
  }, [navigate, clearCachedUser]);

  // ##### LOGOUT AND WIPE - Logout + delete everything this app stored in the browser
  // Full page load at the end so nothing stays in memory either.
  const logoutAndWipe = useCallback(async () => {
    await revokeRefreshToken(localStorage.getItem('refresh_token'), { wipe: true });
    await wipeAllLocalData();
    window.location.replace(routes_web.accounts_login);
  }, []);

  // Function to load the user details from the API (offline-first secure)
  const get_user = useCallback(async () => {
    const access_token = localStorage.getItem('access_token');
    if (!access_token) {
      logout(); // Clean up and redirect
      return;
    }

    // ##### DECODE JWT LOCALLY (safe - only reads payload)
    const payload = decodeJWT(access_token);
    if (!payload) {
      console.error('Invalid token format');
      logout();
      return;
    }

    const isExpired = isTokenExpired(access_token);
    const isOffline = !navigator.onLine;
    const userId = getUserIdFromToken(access_token);

    // ##### OFFLINE-FIRST LOGIC
    if (isOffline) {
      // Offline: Use cache or token payload
      if (isExpired) {
        // Token expired offline → limited access (read-only from cache)
        console.warn('Token expired offline - limited access mode');
        const cachedUser = getCachedUser();
        if (cachedUser) {
          setUser(cachedUser);
          return; // ⚠️ Limited access (read-only)
        }
        // No cache → logout
        logout();
        return;
      } else {
        // Token valid offline → use cache or token payload
        const cachedUser = getCachedUser();
        if (cachedUser) {
          setUser(cachedUser);
        } else {
          // Fallback to token payload (limited data)
          setUser({
            id: userId,
            username: payload.username || payload.email || null,
            email: payload.email || null,
            ...payload
          });
        }
        return;
      }
    }

    // ##### ONLINE: USE CACHE FIRST, THEN VALIDATE IN BACKGROUND
    // Check if we have valid cached user data
    const cachedUser = getCachedUser();
    if (cachedUser && isTokenValidLocally(access_token)) {
      // Use cache immediately (instant response)
      setUser(cachedUser);
      
      // Validate with server in background (non-blocking)
      // This ensures cache is fresh but doesn't block UI
      api.get(routes_api.accounts_getuser)
        .then((response) => {
          // Update with fresh data from server
          setUser(response.data);
          cacheUser(response.data);
        })
        .catch((err) => {
          // If validation fails, keep using cache (already set above)
          console.warn('Background user validation failed, using cache:', err);
        });
      
      return; // Return immediately with cached data
    }

    // No valid cache → fetch from server (blocking)
    try {
      const response = await api.get(routes_api.accounts_getuser);
      setUser(response.data);
      cacheUser(response.data); // Cache user data securely
    } catch (err) {
      console.error("Error loading user:", err);
      
      // ##### DIFFERENTIATE ERROR TYPES
      // Network error (no response) → use cache/token
      if (!err.response || err.response.status === 0) {
        console.warn('Network error - using cached data');
        const cachedUser = getCachedUser();
        if (cachedUser && isTokenValidLocally(access_token)) {
          setUser(cachedUser);
          return;
        }
        // Fallback to token payload if cache unavailable
        if (isTokenValidLocally(access_token)) {
          setUser({
            id: userId,
            username: payload.username || payload.email || null,
            email: payload.email || null,
            ...payload
          });
          return;
        }
      }
      
      // Authentication error (401, 403) → logout
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        console.error('Authentication error - logging out');
        logout();
        return;
      }
      
      // Other errors → logout for safety
      logout();
    }
  }, [logout, getCachedUser, cacheUser]);

  // ##### VALIDATION IN BACKGROUND: Validate/update user data if token exists
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    // If has token, validate/update in background (non-blocking)
    // This ensures cache is fresh and validates token with server
    if (token) {
      // Validate/update in background (doesn't block UI if cache exists)
      get_user();
    }
  }, []); // Only on mount

  const login = async (username, password) => {
    setIsLoginLoading(true);
    setLoginError('');

    try {
      const response = await api.post(routes_api.accounts_login, { username, password });
      
      // ##### USER SWITCH: drop the previous user's offline data (incl. their unsynced queue)
      const userId = getUserIdFromToken(response.data.access);
      const lastUserId = localStorage.getItem(LAST_USER_ID_KEY);
      if (lastUserId && userId != null && lastUserId !== String(userId)) {
        await wipeOfflineData();
      }
      if (userId != null) localStorage.setItem(LAST_USER_ID_KEY, String(userId));

      // Store tokens and update states
      localStorage.setItem('access_token', response.data.access);
      localStorage.setItem('refresh_token', response.data.refresh);
      toast.success('Welcome!');
      navigate(routes_web.accounts_initial);

    } catch (err) {
      // Errors treatment
      if (!err.response) {
        setLoginError('Server offline'); // Server offline
      } else if (err.response.status === 401) {
        setLoginError(
          err.response.data.error === 'user_invalid' ? 'User not found' :
          err.response.data.error === 'pass_invalid' ? 'Incorrect password' :
          'Invalid credentials'
        );
      } else {
        setLoginError('An error occured during login');
      }
    } finally {
      setIsLoginLoading(false);
    }
  };

  // Expose necessary states and functions
  return (
    <AuthContext.Provider value={{ user, login_error, isLoginLoading, login, logout, logoutAndWipe, get_user }}>
      {children}
    </AuthContext.Provider>
  );
};

// Personalized hook for consuming context 
export const useAuth = () => useContext(AuthContext);